import React, { useState, useEffect, useRef, cloneElement, useLayoutEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/utils/cn";
import { useTranslation } from "react-i18next";

interface RenameModalProps {
  trigger: React.ReactElement;
  currentName: string;
  onConfirm: (newName: string) => void;
}

export const RenameModal: React.FC<RenameModalProps> = ({
  trigger,
  currentName,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentName);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    if (open) {
      setName(currentName);
      
      // 计算触发元素的位置并处理边界情况
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const modalWidth = 228; // max-w-[228px]
        const modalHeight = 50; // 估算输入框高度
        const padding = 8; // 边距
        
        let top = rect.top + window.scrollY + 4;
        let left = rect.left + window.scrollX;
        
        // 处理右边界
        if (left + modalWidth > window.innerWidth) {
          left = window.innerWidth - modalWidth - padding;
        }
        
        // 处理左边界
        if (left < padding) {
          left = padding;
        }
        
        // 处理下边界 - 如果下方空间不足，显示在上方
        if (top + modalHeight > window.innerHeight + window.scrollY) {
          top = rect.top + window.scrollY - modalHeight - 4;
        }
        
        // 处理上边界 - 如果上方也不足，强制显示在下方并调整到可见区域
        if (top < window.scrollY + padding) {
          top = window.scrollY + padding;
        }
        
        setPosition({ top, left });
      }
      
      // 延迟聚焦和全选，确保模态框完全打开
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
    }
  }, [open, currentName]);

  const handleSubmit = () => {
    if (name.trim() && name.trim() !== currentName) {
      onConfirm(name.trim());
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  // 监听弹窗关闭事件，除了 ESC 之外的关闭都触发提交
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // 如果是通过其他方式关闭（点击遮罩、点击关闭按钮等），触发提交
      if (name.trim() && name.trim() !== currentName) {
        onConfirm(name.trim());
      }
    }
    setOpen(newOpen);
  };

  const triggerWithClick = cloneElement(trigger, {
    ref: triggerRef,
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setOpen(true);
    }
  });

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        {triggerWithClick}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={cn("fixed inset-0 bg-transparent z-40")} />
        <Dialog.Title className={cn("text-lg font-semibold mb-4")}></Dialog.Title>
        <Dialog.Content
          className={cn(
            "absolute bg-white rounded-md shadow-md p-1.5 min-w-[14.25rem] max-w-[14.25rem] w-full z-50"
          )}
          style={{
            top: position.top,
            left: position.left,
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <form onSubmit={handleFormSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                "w-full px-2.5 py-2 text-sm text-i-normal font-medium bg-i-input rounded-sm focus:outline-none"
              )}
              placeholder={t("rename_modal.placeholder")}
            />
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
